import React, { useState, useCallback } from 'react';
import { CellComponentProps } from '../types';
import { genres } from '../../../utils/constants';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
  CommandEmpty,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface GenreOption {
  value: string;
  label: string;
}

interface GenreMultiSelectProps extends CellComponentProps {
  disabled?: boolean;
  invalid?: boolean;
}

const GenreMultiSelect = React.forwardRef<any, GenreMultiSelectProps>(
  ({ value, onChange, isExpanded = false, disabled = false, invalid = false }, ref) => {
    const currentGenres = value as string[];

    const genreOptions: GenreOption[] = genres.map((genre) => ({
      value: genre,
      label: genre.charAt(0).toUpperCase() + genre.slice(1).replace('-', ' '),
    }));

    const updateGenres = useCallback(
      (newGenres: GenreOption[]) => {
        const genreValues = newGenres.map((genre) => genre.value);
        onChange(genreValues);
      },
      [onChange],
    );

    const currentGenreOptions: GenreOption[] = currentGenres.map((genre) => ({
      value: genre,
      label: genre.charAt(0).toUpperCase() + genre.slice(1).replace('-', ' '),
    }));

    const renderGenreItem = useCallback(
      (genre: GenreOption, isSelected: boolean, onClick: () => void) => (
        <CommandItem
          key={genre.value}
          value={genre.value}
          onSelect={onClick}
          className="tw-flex tw-items-center tw-justify-between tw-p-2 tw-cursor-pointer hover:tw-bg-gray-100"
        >
          <span className="tw-text-sm">{genre.label}</span>
          <div
            className={cn(
              'tw-h-4 tw-w-4 tw-border tw-rounded-sm tw-flex tw-items-center tw-justify-center',
              isSelected
                ? 'tw-bg-primary tw-border-primary'
                : 'tw-border-input',
            )}
          >
            {isSelected && (
              <Check className="tw-h-3 tw-w-3 tw-text-primary-foreground" />
            )}
          </div>
        </CommandItem>
      ),
      [],
    );

    const renderGenreChip = useCallback(
      (genre: GenreOption, onRemove: (key: string) => void) => (
        <Badge
          key={genre.value}
          variant="default"
          className="tw-flex tw-items-center tw-gap-1 tw-max-w-[150px] tw-bg-secondary tw-text-secondary-foreground hover:tw-bg-secondary/80 tw-transition-colors"
        >
          <span className="tw-truncate tw-text-xs">{genre.label}</span>
          {!disabled && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove(genre.value);
              }}
              type="button"
              className="tw-ml-1 tw-h-3 tw-w-3 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-secondary-foreground/70 hover:tw-text-secondary-foreground hover:tw-bg-secondary-foreground/20 tw-transition-colors"
            >
              <X className="tw-h-2 tw-w-2" />
            </button>
          )}
        </Badge>
      ),
      [disabled],
    );

    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    // Filter genres based on search
    const filteredGenres = genreOptions.filter((genre) =>
      genre.label.toLowerCase().includes(searchValue.toLowerCase()),
    );

    const availableGenres = filteredGenres.filter(
      (genre) => !currentGenres.some((selected) => selected === genre.value),
    );

    // Reset search when popover closes
    const handleOpenChange = useCallback((newOpen: boolean) => {
      setOpen(newOpen);
      if (!newOpen) {
        setSearchValue('');
      }
    }, []);

    const displayText =
      currentGenres.length === 0
        ? 'Select genres...'
        : `${currentGenres.length} genre${currentGenres.length !== 1 ? 's' : ''} selected`;

    // Expose focus method via ref
    React.useImperativeHandle(ref, () => ({
      focus: () => {
        setOpen(true);
      },
    }));

    if (!isExpanded) {
      return (
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className={cn(
                'tw-w-full tw-justify-between tw-h-auto tw-min-h-[2.5rem] tw-text-sm',
                invalid && '!tw-border-red-500',
              )}
              disabled={disabled}
            >
              <span
                className={cn(
                  currentGenres.length === 0
                    ? 'tw-text-muted-foreground'
                    : 'tw-text-foreground',
                )}
              >
                {displayText}
              </span>
              <ChevronsUpDown className="tw-ml-2 tw-h-4 tw-w-4 tw-shrink-0 tw-opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="tw-w-48 tw-p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search genres..."
                value={searchValue}
                onValueChange={setSearchValue}
                className="tw-border-none focus:tw-ring-0 focus:tw-border-none focus:tw-outline-none !tw-border-b-0"
              />
              <CommandList className="tw-max-h-60">
                {currentGenres.length === 0 && availableGenres.length === 0 && (
                  <CommandEmpty>No genres found</CommandEmpty>
                )}
                {currentGenres.length > 0 && (
                  <div className="tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-muted-foreground tw-border-b">
                    Selected ({currentGenres.length})
                  </div>
                )}
                {currentGenreOptions.map((genre) =>
                  renderGenreItem(genre, true, () => {
                    const newGenres = currentGenres.filter(
                      (g) => g !== genre.value,
                    );
                    onChange(newGenres);
                  }),
                )}
                {availableGenres.length > 0 && (
                  <div className="tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-muted-foreground tw-border-b">
                    Available
                  </div>
                )}
                {availableGenres.map((genre) =>
                  renderGenreItem(genre, false, () => {
                    onChange([...currentGenres, genre.value]);
                  }),
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      );
    }

    return (
      <div className="tw-space-y-2">
        {!disabled && (
          <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className={cn(
                  'tw-w-full tw-justify-between tw-h-auto tw-min-h-[2.5rem] tw-text-sm',
                  invalid && '!tw-border-red-500',
                )}
                disabled={disabled}
              >
                <span
                  className={cn(
                    currentGenres.length === 0
                      ? 'tw-text-muted-foreground'
                      : 'tw-text-foreground',
                  )}
                >
                  {currentGenres.length === 0 ? 'Select...' : 'Add more...'}
                </span>
                <ChevronsUpDown className="tw-ml-2 tw-h-4 tw-w-4 tw-shrink-0 tw-opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="tw-w-32 !tw-p-0" align="start">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search genres..."
                  value={searchValue}
                  onValueChange={setSearchValue}
                  className="tw-border-none focus:tw-ring-0 focus:tw-border-none focus:tw-outline-none !tw-border-b-0"
                />
                <CommandList className="tw-max-h-60">
                  {currentGenres.length === 0 &&
                    availableGenres.length === 0 && (
                      <CommandEmpty>No matching genres found</CommandEmpty>
                    )}
                  {currentGenres.length > 0 && (
                    <div className="tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-muted-foreground tw-border-b">
                      Selected ({currentGenres.length})
                    </div>
                  )}
                  {currentGenreOptions.map((genre) =>
                    renderGenreItem(genre, true, () => {
                      const newGenres = currentGenres.filter(
                        (g) => g !== genre.value,
                      );
                      onChange(newGenres);
                    }),
                  )}
                  {availableGenres.length > 0 && (
                    <div className="tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-muted-foreground tw-border-b">
                      Available
                    </div>
                  )}
                  {availableGenres.map((genre) =>
                    renderGenreItem(genre, false, () => {
                      onChange([...currentGenres, genre.value]);
                    }),
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}

        {currentGenres.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-gap-1">
            {currentGenreOptions.map((genre) =>
              renderGenreChip(genre, (value) => {
                const newGenres = currentGenres.filter((g) => g !== value);
                onChange(newGenres);
              }),
            )}
          </div>
        )}
      </div>
    );
  },
);

export default GenreMultiSelect;
